(() => {
  if (window.__customFullscreenHeroLoaded) return;
  window.__customFullscreenHeroLoaded = true;
  const selector = '[data-custom-fullscreen-hero]';
  const instances = new Map();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CustomFullscreenHero {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.observers = [];
      this.initAnimations();
      this.initVideos();
      this.initScrollLinks();
      this.initEqualButtonSpacing();
    }

    initAnimations() {
      const blocks = [...this.root.querySelectorAll('.cfh-animate')];
      if (!blocks.length || reduceMotion() || !('IntersectionObserver' in window)) {
        blocks.forEach((block) => block.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      blocks.forEach((block) => observer.observe(block));
      this.observers.push(observer);
    }

    initVideos() {
      const videos = [...this.root.querySelectorAll('.custom-fullscreen-hero__video')];
      if (!videos.length) return;
      if (reduceMotion()) {
        videos.forEach((video) => video.pause());
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && this.root.dataset.videoAutoplay === 'true' && video.muted) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.08 });
      videos.forEach((video) => observer.observe(video));
      this.observers.push(observer);
    }

    initScrollLinks() {
      this.root.querySelectorAll('[data-cfh-scroll]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const next = this.root.closest('.shopify-section')?.nextElementSibling || this.root.parentElement?.nextElementSibling;
          if (!next) return;
          event.preventDefault();
          next.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
          const focusable = next.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (focusable) window.setTimeout(() => focusable.focus({ preventScroll: true }), reduceMotion() ? 0 : 500);
        }, { signal: this.abort.signal });
      });
    }

    initEqualButtonSpacing() {
      this.equalizeFrame = 0;
      this.queueEqualize = () => {
        if (!this.root.isConnected) return;
        window.cancelAnimationFrame(this.equalizeFrame);
        this.equalizeFrame = window.requestAnimationFrame(() => this.equalizeButtonSpacing());
      };
      window.addEventListener('resize', this.queueEqualize, { signal: this.abort.signal });
      if (document.fonts?.ready) document.fonts.ready.then(this.queueEqualize).catch(() => {});
      this.queueEqualize();
    }

    equalizeButtonSpacing() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const enabled = mobile
        ? this.root.dataset.equalizeMobileButtons === 'true'
        : this.root.dataset.equalizeDesktopButtons === 'true';
      const blocks = [...this.root.querySelectorAll('.custom-fullscreen-hero__block--button')];

      blocks.forEach((block) => {
        block.classList.remove('cfh-equal-button-gap');
        block.style.removeProperty('--cfh-equal-left');
      });
      if (!enabled) return;

      const visible = blocks.filter((block) => !block.hidden && getComputedStyle(block).display !== 'none');
      if (visible.length < 2) return;

      const rootRect = this.root.getBoundingClientRect();
      const links = visible.map((block) => block.querySelector('.custom-fullscreen-hero__button'));
      if (links.some((link) => !link)) return;

      const widths = links.map((link) => link.getBoundingClientRect().width);
      const requestedGap = Number(mobile ? this.root.dataset.mobileButtonGap : this.root.dataset.desktopButtonGap) || 0;
      const rootStyles = getComputedStyle(this.root);
      const safe = Number.parseFloat(rootStyles.getPropertyValue('--cfh-safe')) || 0;
      const available = Math.max(0, rootRect.width - (safe * 2));
      const widthTotal = widths.reduce((total, width) => total + width, 0);
      const maximumGap = visible.length > 1 ? Math.max(0, (available - widthTotal) / (visible.length - 1)) : 0;
      const gap = Math.min(requestedGap, maximumGap);
      const rowWidth = widthTotal + (gap * (visible.length - 1));
      const originalStart = links[0].getBoundingClientRect().left - rootRect.left;
      const start = Math.max(safe, Math.min(originalStart, Math.max(safe, rootRect.width - safe - rowWidth)));

      visible.forEach((block) => {
        block.style.setProperty('--cfh-equal-left', '0px');
        block.classList.add('cfh-equal-button-gap');
      });

      let cursor = start;
      visible.forEach((block, index) => {
        const link = links[index];
        const blockRect = block.getBoundingClientRect();
        const linkOffset = link.getBoundingClientRect().left - blockRect.left;
        block.style.setProperty('--cfh-equal-left', `${cursor - linkOffset}px`);
        cursor += widths[index] + gap;
      });
    }

    revealBlock(blockId, selected) {
      const block = this.root.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
      if (!block) return;
      block.classList.toggle('shopify-block-selected', selected);
      if (selected) {
        block.classList.add('is-visible');
        block.hidden = false;
      }
    }

    destroy() {
      window.cancelAnimationFrame(this.equalizeFrame);
      this.abort.abort();
      this.observers.forEach((observer) => observer.disconnect());
      this.observers = [];
    }
  }

  const init = (scope = document) => {
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach((root) => {
      if (instances.has(root.id)) instances.get(root.id).destroy();
      instances.set(root.id, new CustomFullscreenHero(root));
    });
  };

  const unload = (sectionId) => {
    const id = `CustomFullscreenHero-${sectionId}`;
    instances.get(id)?.destroy();
    instances.delete(id);
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.detail.sectionId));
  document.addEventListener('shopify:block:select', (event) => {
    instances.get(`CustomFullscreenHero-${event.detail.sectionId}`)?.revealBlock(event.detail.blockId, true);
  });
  document.addEventListener('shopify:block:deselect', (event) => instances.get(`CustomFullscreenHero-${event.detail.sectionId}`)?.revealBlock(event.detail.blockId, false));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
